import React from "react";
import { Box, Typography, InputBase } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const SearchIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const TABS = [

  { id: "needs_pmo", label: "At PMO", summaryKey: "needsPmo" },
  { id: "at_scg", label: "At SCG", summaryKey: "atScg" },
  { id: "approved", label: "Approved", summaryKey: "approved" },
  { id: "rejected", label: "Rejected", summaryKey: "rejected" },
];

const ApprovalFilters = ({
  summary,
  activeFilter,
  onFilterChange,
  search,
  onSearchChange,
  approvedSubFilter = "all",
  onApprovedSubFilterChange,
  activeApprovedCount,
  endedApprovedCount,
}) => {
  const theme = useTheme();
  const t = theme.custom;

  return (
    <Box sx={{
      display: "flex", alignItems: "center",
      padding: "0 20px",
      borderBottom: `1px solid ${t.border2}`,
      flexShrink: 0, gap: "0",
    }}>

      {/* Filter tabs */}
      <Box sx={{ display: "flex", alignItems: "stretch", flex: 1, height: "44px" }}>
        {TABS.map((tab) => {
          const isActive = activeFilter === tab.id;
          const count = summary?.[tab.summaryKey] ?? 0;

          return (
            <Box
              key={tab.id}
              onClick={() => onFilterChange(tab.id)}
              sx={{
                display: "flex", alignItems: "center", gap: "6px",
                padding: "0 16px", cursor: "pointer", flexShrink: 0,
                borderBottom: isActive ? `2px solid ${t.accent}` : "2px solid transparent",
                transition: "all 0.15s",
                "&:hover": { background: t.surface2 },
              }}
            >
              <Typography sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "12px",
                fontWeight: isActive ? 600 : 400,
                color: tab.id === "rejected"
                  ? "#c84040"
                  : isActive ? t.accent : t.text2,
                whiteSpace: "nowrap",
              }}>
                {tab.label}
              </Typography>
              <Box sx={{
                background: isActive ? t.accent2 : t.surface3,
                border: isActive ? `1px solid ${t.accent}44` : `1px solid ${t.border}`,
                borderRadius: "3px", padding: "1px 6px",
              }}>
                <Typography sx={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "9px", fontWeight: 600,
                  color: isActive ? t.accent : t.text3,
                }}>
                  {count}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Sub-filter chips for Approved tab */}
      {activeFilter === "approved" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mr: "16px", flexShrink: 0 }}>
          {[
            { id: "all", label: "All", count: (activeApprovedCount ?? 0) + (endedApprovedCount ?? 0) },
            { id: "active", label: "Active", count: activeApprovedCount ?? 0, color: "#4a9960" },
            { id: "ended", label: "Ended", count: endedApprovedCount ?? 0, color: "#7A9E9C" },
          ].map((sub) => {
            const isSubActive = approvedSubFilter === sub.id;
            return (
              <Box
                key={sub.id}
                onClick={() => onApprovedSubFilterChange?.(sub.id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "4px 10px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  background: isSubActive
                    ? (sub.color ? `${sub.color}22` : t.accent2)
                    : t.surface2,
                  border: isSubActive
                    ? `1px solid ${sub.color || t.accent}`
                    : `1px solid ${t.border2}`,
                  transition: "all 0.15s ease",
                  "&:hover": {
                    background: isSubActive ? undefined : t.surface3,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "11px",
                    fontWeight: isSubActive ? 600 : 400,
                    color: isSubActive ? (sub.color || t.accent) : t.text2,
                  }}
                >
                  {sub.label}
                </Typography>
                <Box
                  sx={{
                    background: isSubActive
                      ? (sub.color ? `${sub.color}33` : `${t.accent}33`)
                      : t.surface3,
                    borderRadius: "3px",
                    padding: "0px 5px",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "9px",
                      fontWeight: 600,
                      color: isSubActive ? (sub.color || t.accent) : t.text3,
                    }}
                  >
                    {sub.count}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Search */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: "7px",
        background: t.surface2, border: `1px solid ${t.border2}`,
        borderRadius: "4px", padding: "6px 10px", width: "240px",
        flexShrink: 0,
      }}>
        <Box sx={{ color: t.text3, flexShrink: 0 }}><SearchIcon /></Box>
        <InputBase
          placeholder="Search name, SOW, account, code..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{
            flex: 1, fontFamily: "'Inter', sans-serif",
            fontSize: "11px", color: t.text,
            "& input::placeholder": { color: t.text3 },
          }}
        />
      </Box>
    </Box>
  );
};

export default ApprovalFilters;